import { useMutation, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { toast } from 'react-toastify';
import type { FacilitySpecialty } from '../../../../js/api/models/FacilitySpecialty';
import { HealthcareEntitiesService } from '../../../../js/api/services/HealthcareEntitiesService';
import { handleApiError } from '../../../../js/app/utils/errorhandling';
import { Button } from '../../components/button';
import { Dialog, DialogTitle, DialogBody, DialogActions } from '../../components/dialog';
import { useCodeDefinitionsByCategories } from '../../hooks/useCodeDefinitions';
import { DynamicFieldFactory } from '../../utils/DynamicFieldFactory';

interface EditSpecialtyDialogProps {
  open: boolean;
  onClose: () => void;
  specialty: FacilitySpecialty & { title?: string };
  onSave: (specialtyId: string, dynamicFields: any) => Promise<void>;
  onSuccess: () => void;
}

export const EditSpecialtyDialog: React.FC<EditSpecialtyDialogProps> = ({
  open,
  onClose,
  specialty,
  onSave,
  onSuccess,
}) => {
  const [dynamicFields, setDynamicFields] = React.useState<any[]>([]);

  const queryClient = useQueryClient();

  const dynamicChoicesCategories = React.useMemo(() => {
    if (!specialty?.dynamic_fields) return [];
    const fields =
      typeof specialty.dynamic_fields === 'string'
        ? JSON.parse(specialty.dynamic_fields)
        : specialty.dynamic_fields;
    return fields
      .filter(
        (field: any) =>
          (field.field_type === 'select' || field.field_type === 'multiselect') &&
          field.choices_category
      )
      .map((field: any) => field.choices_category);
  }, [specialty?.dynamic_fields]);

  const { data: codeDefinitionsByCategory, isLoading } =
    useCodeDefinitionsByCategories(dynamicChoicesCategories);

  const dynamicFieldChoices = React.useMemo(() => {
    if (!codeDefinitionsByCategory) return {};
    const choicesMap: Record<string, any[]> = {};
    dynamicChoicesCategories.forEach((category: string) => {
      choicesMap[category] = codeDefinitionsByCategory[category] || [];
    });
    return choicesMap;
  }, [codeDefinitionsByCategory, dynamicChoicesCategories]);

  const mutation = useMutation({
    mutationFn: async (data: any) =>
      HealthcareEntitiesService.healthcareEntitiesFacilitySpecialtiesPartialUpdate({
        id: specialty.id,
        requestBody: data,
      }),
    onSuccess: () => {
      toast.success('Specialty updated successfully!');
      queryClient.invalidateQueries();
      onClose();
      onSuccess();
    },
    onError: (error: any) => {
      handleApiError(error, { messagePrefix: 'Failed to update specialty' });
    },
  });

  // Reset form state when dialog opens
  const previousOpenRef = React.useRef(false);
  React.useEffect(() => {
    // Only reset when dialog transitions from closed to open
    if (open && !previousOpenRef.current) {
      if (specialty?.dynamic_fields) {
        const fields =
          typeof specialty.dynamic_fields === 'string'
            ? JSON.parse(specialty.dynamic_fields)
            : specialty.dynamic_fields;
        setDynamicFields(fields);
      } else {
        setDynamicFields([]);
      }
    }
    previousOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleFieldChange = (fieldName: string, value: any) => {
    setDynamicFields((prev) =>
      prev.map((field) =>
        field.field_name === fieldName ? { ...field, field_value: value } : field
      )
    );
  };

  const handleSave = () => {
    mutation.mutate({
      dynamic_fields: dynamicFields.map((field) => {
        // TODO: this key name 'choices' must be consistent with key in enrichDynamicFieldsWithChoices
        // need to move to a constant that both import and use
        const { choices: _choices, ...fieldWithoutChoices } = field;
        return {
          ...fieldWithoutChoices,
          field_value:
            field.field_type === 'number'
              ? field.field_value === '' || field.field_value === null
                ? null
                : Number(field.field_value)
              : field.field_value,
        };
      }),
    });
  };

  if (!open) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle className="tw:dark:!text-white/90">
        Edit {specialty.title || specialty.specialty}
      </DialogTitle>
      <DialogBody>
        {isLoading ? (
          <div className="tw:flex tw:items-center tw:justify-center tw:py-8">
            <div className="tw:animate-spin tw:rounded-full tw:h-8 tw:w-8 tw:border-b-2 tw:border-brand-green-0" />
          </div>
        ) : dynamicFields.length > 0 ? (
          <DynamicFieldFactory
            fields={dynamicFields.map((field) => ({
              ...field,
              choices:
                field.field_type === 'select' || field.field_type === 'multiselect'
                  ? dynamicFieldChoices[field.choices_category] || []
                  : undefined,
            }))}
            onChange={handleFieldChange}
            columns={2}
          />
        ) : (
          <div className="tw:flex tw:flex-row tw:items-center tw:justify-center tw:py-8 tw:text-zinc-600 tw:dark:text-zinc-400">
            <div className="tw:text-center">
              <i className="fas fa-info-circle tw:text-2xl tw:mb-2 tw:text-brand-green-0 tw:dark:text-brand-green-1 me-2" />
              No editable fields for this specialty.
            </div>
          </div>
        )}
      </DialogBody>
      <DialogActions>
        <Button type="button" plain onClick={onClose}>
          Cancel
        </Button>
        {dynamicFields.length > 0 && (
          <Button
            type="button"
            color="brandgreen0"
            onClick={handleSave}
            disabled={mutation.isLoading || isLoading}
          >
            {mutation.isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
